/**
 * Hook to fetch and transform screening questions from the API.
 *
 * This hook fetches the case-type-specific screening configuration from the backend
 * and transforms it into the frontend ScreeningCategory[] format used by the UI.
 */

import { useMemo } from 'react';

import {
  CategoryConfig,
  InputFieldConfig,
  QuestionConfig,
} from '@/services/types/screening-config-types';

import { useGetScreeningConfigByCaseTypeQuery } from '../../../../services/api/screeningConfigService';
import {
  ScreeningCategory,
  ScreeningInputField,
  ScreeningMainQuestion,
  ScreeningResponseOption,
  ScreeningResponseType,
  ScreeningSubQuestion,
} from '../types';

interface UseScreeningQuestionsProps {
  caseType: string;
  firmId?: string;
}

interface UseScreeningQuestionsResult {
  questions: ScreeningCategory[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  /** The raw config from the API (useful for sync operations) */
  rawConfig?: ReturnType<typeof useGetScreeningConfigByCaseTypeQuery>['data'];
}

/**
 * Transform backend InputFieldConfig[] to frontend text field labels.
 * For simple text/textarea fields, extract just the labels.
 */
function extractTextFieldLabels(inputFields: InputFieldConfig[]): string[] {
  return inputFields
    .filter(
      (f) =>
        f.field_type === 'text' ||
        f.field_type === 'textarea' ||
        f.field_type === 'date' ||
        f.field_type === 'number',
    )
    .sort((a, b) => a.display_order - b.display_order)
    .map((f) => f.label);
}

/**
 * Transform backend InputFieldConfig[] to frontend ScreeningInputField[].
 * Includes all field types including dropdowns.
 */
function transformInputFields(
  inputFields: InputFieldConfig[],
): ScreeningInputField[] {
  return inputFields
    .slice()
    .sort((a, b) => a.display_order - b.display_order)
    .map((f) => ({
      key: f.key,
      label: f.label,
      fieldType: f.field_type,
      placeholder: f.placeholder ?? undefined,
      isRequired: f.is_required,
      options: f.options?.map((o) => ({
        value: o.value,
        label: o.label,
        triggersEscalation: o.triggers_escalation,
        triggersDisqualification: o.triggers_disqualification,
      })),
      showWhenAnswer: f.show_when_answer ?? 'always',
    }));
}

/**
 * Check if any input field should always be shown (not conditional on answer).
 */
function hasAlwaysVisibleFields(inputFields: InputFieldConfig[]): boolean {
  return inputFields.some((f) => !f.show_when_field);
}

/**
 * Transform backend response options to frontend format.
 */
function transformResponseOptions(
  options: QuestionConfig['response_options'],
): ScreeningResponseOption[] {
  if (!options) return [];
  return options.map((o) => ({
    value: o.value,
    label: o.label,
    triggersEscalation: o.triggers_escalation,
    triggersDisqualification: o.triggers_disqualification,
  }));
}

/**
 * Get the placeholder from the first textarea field.
 */
function getTextFieldPlaceholder(
  inputFields: InputFieldConfig[],
): string | undefined {
  const textareaField = inputFields.find((f) => f.field_type === 'textarea');
  return textareaField?.placeholder ?? undefined;
}

/**
 * Get the label from the first text/textarea field for single-field questions.
 */
function getTextFieldLabel(
  inputFields: InputFieldConfig[],
): string | undefined {
  const field = inputFields.find(
    (f) => f.field_type === 'text' || f.field_type === 'textarea',
  );
  return field?.label;
}

/**
 * Determine when to show the text field based on the first field's show_when config.
 */
function getShowTextFieldOnAnswer(
  inputFields: InputFieldConfig[],
): 'yes' | 'no' | undefined {
  const field = inputFields.find((f) => f.show_when_field && f.show_when_value);
  if (!field) return undefined;

  const showWhenValues = Array.isArray(field.show_when_value)
    ? field.show_when_value
    : [field.show_when_value];

  if (showWhenValues.includes('yes')) return 'yes';
  if (showWhenValues.includes('no')) return 'no';
  return undefined;
}

/**
 * Transform a backend QuestionConfig to a frontend ScreeningSubQuestion.
 */
function transformSubQuestion(question: QuestionConfig): ScreeningSubQuestion {
  const hasTextFields = question.input_fields.length > 0;
  const textFieldLabels = extractTextFieldLabels(question.input_fields);
  const inputFields = transformInputFields(question.input_fields);

  // Check if this question triggers escalation (from dropdown options or response options)
  const hasEscalationTrigger =
    question.input_fields.some((f) =>
      f.options?.some((o) => o.triggers_escalation),
    ) || question.response_options?.some((o) => o.triggers_escalation);

  return {
    id: question.key,
    text: question.text,
    isOverrideTrigger: hasEscalationTrigger,
    hasTextFields: hasTextFields && textFieldLabels.length > 0,
    textFieldLabels: textFieldLabels.length > 0 ? textFieldLabels : undefined,
    inputFields: inputFields.length > 0 ? inputFields : undefined,
    responseType: (question.response_type as ScreeningResponseType) || 'toggle',
    responseOptions: transformResponseOptions(question.response_options),
  };
}

/**
 * Transform a backend QuestionConfig to a frontend ScreeningMainQuestion.
 */
function transformMainQuestion(
  question: QuestionConfig,
): ScreeningMainQuestion {
  const rawInputFields = question.input_fields;
  const hasTextField = rawInputFields.length > 0;
  const textFieldLabels = extractTextFieldLabels(rawInputFields);
  const isSingleField = textFieldLabels.length === 1;
  const hasMultipleFields = textFieldLabels.length > 1;

  // When using legacy textFieldLabels mode (multiple text fields like 3P/1P),
  // only include dropdown/multi_select in inputFields to avoid duplicate rendering.
  // When not using legacy mode, include all fields in inputFields.
  const inputFieldsToInclude = hasMultipleFields
    ? rawInputFields.filter(
        (f) => f.field_type === 'dropdown' || f.field_type === 'multi_select',
      )
    : isSingleField
      ? rawInputFields.filter(
          (f) => f.field_type === 'dropdown' || f.field_type === 'multi_select',
        )
      : rawInputFields;
  const inputFields = transformInputFields(inputFieldsToInclude);

  return {
    id: question.key,
    text: question.text,
    hasTextField,
    // Single field mode
    textFieldPlaceholder: isSingleField
      ? getTextFieldPlaceholder(rawInputFields)
      : undefined,
    textFieldLabel: isSingleField
      ? getTextFieldLabel(rawInputFields)
      : undefined,
    // Multiple fields mode
    textFieldLabels: hasMultipleFields ? textFieldLabels : undefined,
    alwaysShowTextFields: hasAlwaysVisibleFields(rawInputFields),
    showTextFieldOnAnswer: getShowTextFieldOnAnswer(rawInputFields),
    // Full input field configs (only dropdowns when using legacy text field mode)
    inputFields: inputFields.length > 0 ? inputFields : undefined,
    // Response type for the question itself
    responseType: (question.response_type as ScreeningResponseType) || 'toggle',
    responseOptions: transformResponseOptions(question.response_options),
  };
}

/**
 * Transform a backend CategoryConfig to a frontend ScreeningCategory.
 */
function transformCategory(category: CategoryConfig): ScreeningCategory {
  const subQuestions = category.sub_questions
    .filter((q) => q.show_when === 'yes')
    .sort(
      (a, b) =>
        category.sub_questions.indexOf(a) - category.sub_questions.indexOf(b),
    )
    .map(transformSubQuestion);

  const backupQuestions = category.backup_questions
    .filter((q) => q.show_when === 'no')
    .map(transformSubQuestion);

  return {
    id: category.category_type,
    letter: category.letter,
    mainQuestion: transformMainQuestion(category.main_question),
    subQuestionsOnYes: subQuestions.length > 0 ? subQuestions : undefined,
    backupQuestionsOnNo:
      backupQuestions.length > 0 ? backupQuestions : undefined,
  };
}

/**
 * Hook to fetch screening questions for a specific case type.
 *
 * @param caseType - The case type to fetch questions for (e.g., 'mva', 'premises_liability')
 * @param firmId - Optional firm ID for firm-specific overrides
 * @returns Transformed screening questions and loading/error state
 *
 * @example
 * ```tsx
 * const { questions, isLoading, isError } = useScreeningQuestions({
 *   caseType: 'mva',
 *   firmId: firm?.id
 * });
 *
 * if (isLoading) return <Spinner />;
 * if (isError) return <ErrorState />;
 *
 * return <ScreeningChecklist questions={questions} />;
 * ```
 */
export function useScreeningQuestions({
  caseType,
  firmId,
}: UseScreeningQuestionsProps): UseScreeningQuestionsResult {
  // Only include firm_id if it's a valid non-empty string
  const validFirmId = firmId && firmId.trim() ? firmId : undefined;

  const {
    data: config,
    isLoading,
    isError,
    error,
  } = useGetScreeningConfigByCaseTypeQuery(
    { case_type: caseType, firm_id: validFirmId },
    { skip: !caseType },
  );

  const questions = useMemo(() => {
    if (!config) return [];

    return config.categories
      .slice()
      .sort((a, b) => a.display_order - b.display_order)
      .map(transformCategory);
  }, [config]);

  return {
    questions,
    isLoading,
    isError,
    error,
    rawConfig: config,
  };
}

export type { UseScreeningQuestionsResult };
